import React, { useState } from "react";
import {
  Building2,
  Clock,
  Phone,
  Mail,
  Camera,
  Save,
  MapPin,
  Edit3,
  Check,
  X,
} from "lucide-react";
import "../styles/06-pages/dashboard.scss";

interface OpeningHours {
  day: string;
  open: string;
  close: string;
  closed: boolean;
}

interface CompanyProfile {
  id: string;
  name: string;
  address: string;
  city: string;
  email: string;
  phone: string;
  description: string;
  profileImage: string;
  categories: string[];
  isVerified: boolean;
  openingHours: OpeningHours[];
}

const Dashboard: React.FC = () => {
  const showToast = (title: string, description: string) => {
    // Simple toast implementation - you can replace with your preferred toast solution
    alert(`${title}: ${description}`);
  };
  const [activeTab, setActiveTab] = useState("profile");
  const [isEditing, setIsEditing] = useState({
    profile: false,
    hours: false,
    contact: false,
  });

  // Mock data - replace with actual API call
  const [company, setCompany] = useState<CompanyProfile>({
    id: "1",
    name: "Bowling-Center Göttingen",
    address: "Werner-von-Siemens-Straße 10",
    city: "37077 Göttingen",
    email: "info@bowling-goettingen.de",
    phone: "03605 517263",
    description:
      "Modernes Bowling-Center mit 12 Bahnen und gemütlicher Atmosphäre.",
    profileImage: "",
    categories: ["Bowling", "Billard"],
    isVerified: true,
    openingHours: [
      { day: "Montag", open: "15:00", close: "22:00", closed: true },
      { day: "Dienstag", open: "15:00", close: "22:00", closed: false },
      { day: "Mittwoch", open: "15:00", close: "22:00", closed: false },
      { day: "Donnerstag", open: "15:00", close: "23:00", closed: false },
      { day: "Freitag", open: "15:00", close: "00:00", closed: false },
      { day: "Samstag", open: "15:00", close: "00:00", closed: false },
      { day: "Sonntag", open: "11:00", close: "20:00", closed: false },
    ],
  });

  const [editForm, setEditForm] = useState(company);

  const handleSave = (section: "profile" | "hours" | "contact") => {
    setCompany(editForm);
    setIsEditing((prev) => ({ ...prev, [section]: false }));
    showToast(
      "Erfolgreich gespeichert",
      "Ihre Änderungen wurden erfolgreich gespeichert."
    );
  };

  const handleCancel = (section: "profile" | "hours" | "contact") => {
    setEditForm(company);
    setIsEditing((prev) => ({ ...prev, [section]: false }));
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageUrl = e.target?.result as string;
        setEditForm((prev) => ({ ...prev, profileImage: imageUrl }));
        setCompany((prev) => ({ ...prev, profileImage: imageUrl }));
        showToast(
          "Profilbild aktualisiert",
          "Ihr neues Profilbild wurde erfolgreich hochgeladen."
        );
      };
      reader.readAsDataURL(file);
    }
  };

  const updateOpeningHours = (
    index: number,
    field: keyof OpeningHours,
    value: string | boolean
  ) => {
    const newHours = [...editForm.openingHours];
    newHours[index] = { ...newHours[index], [field]: value };
    setEditForm((prev) => ({ ...prev, openingHours: newHours }));
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <div className="company-info">
            <div className="avatar-section">
              <div className="company-avatar">
                {company.profileImage ? (
                  <img src={company.profileImage} alt={company.name} />
                ) : (
                  <div className="avatar-fallback">
                    <Building2 className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="avatar-overlay">
                <label htmlFor="image-upload" className="upload-button">
                  <Camera className="h-4 w-4" />
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </label>
              </div>
            </div>
            <div className="company-details">
              <h1 className="company-name">{company.name}</h1>
              <div className="company-address">
                <MapPin className="location-icon" />
                <span>
                  {company.address}, {company.city}
                </span>
              </div>
              <div className="company-badges">
                {company.categories.map((category) => (
                  <span key={category} className="category-badge">
                    {category}
                  </span>
                ))}
                {company.isVerified && (
                  <span className="verified-badge">
                    <Check className="h-3 w-3" style={{ marginRight: "4px" }} />
                    Verifiziert
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="dashboard-tabs">
          <div className="tabs-list">
            <button
              className={`tab-trigger ${
                activeTab === "profile" ? "active" : ""
              }`}
              onClick={() => setActiveTab("profile")}
              data-state={activeTab === "profile" ? "active" : "inactive"}
            >
              <Building2 className="h-4 w-4" />
              Unternehmensprofil
            </button>
            <button
              className={`tab-trigger ${activeTab === "hours" ? "active" : ""}`}
              onClick={() => setActiveTab("hours")}
              data-state={activeTab === "hours" ? "active" : "inactive"}
            >
              <Clock className="h-4 w-4" />
              Öffnungszeiten
            </button>
            <button
              className={`tab-trigger ${
                activeTab === "contact" ? "active" : ""
              }`}
              onClick={() => setActiveTab("contact")}
              data-state={activeTab === "contact" ? "active" : "inactive"}
            >
              <Phone className="h-4 w-4" />
              Kontaktdaten
            </button>
          </div>

          {activeTab === "profile" && (
            <div className="tab-content">
              <div className="settings-card">
                <div className="card-header">
                  <div className="header-with-action">
                    <h3>Unternehmensprofil</h3>
                    {!isEditing.profile ? (
                      <button
                        onClick={() =>
                          setIsEditing((prev) => ({ ...prev, profile: true }))
                        }
                        className="edit-button"
                      >
                        <Edit3 className="h-4 w-4" />
                        Bearbeiten
                      </button>
                    ) : (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleCancel("profile")}
                          className="cancel-button"
                        >
                          <X className="h-4 w-4" />
                          Abbrechen
                        </button>
                        <button
                          onClick={() => handleSave("profile")}
                          className="save-button"
                        >
                          <Save className="h-4 w-4" />
                          Speichern
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-content">
                  <div className="form-grid">
                    <div className="form-group">
                      <label htmlFor="company-name" className="form-label">
                        Unternehmensname
                      </label>
                      {isEditing.profile ? (
                        <input
                          id="company-name"
                          type="text"
                          value={editForm.name}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              name: e.target.value,
                            }))
                          }
                          className="form-input"
                        />
                      ) : (
                        <p className="form-value">{company.name}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="company-address" className="form-label">
                        Adresse
                      </label>
                      {isEditing.profile ? (
                        <input
                          id="company-address"
                          type="text"
                          value={editForm.address}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              address: e.target.value,
                            }))
                          }
                          className="form-input"
                        />
                      ) : (
                        <p className="form-value">{company.address}</p>
                      )}
                    </div>

                    <div className="form-group">
                      <label htmlFor="company-city" className="form-label">
                        Stadt
                      </label>
                      {isEditing.profile ? (
                        <input
                          id="company-city"
                          type="text"
                          value={editForm.city}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              city: e.target.value,
                            }))
                          }
                          className="form-input"
                        />
                      ) : (
                        <p className="form-value">{company.city}</p>
                      )}
                    </div>

                    <div className="form-group full-width">
                      <label
                        htmlFor="company-description"
                        className="form-label"
                      >
                        Beschreibung
                      </label>
                      {isEditing.profile ? (
                        <textarea
                          id="company-description"
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm((prev) => ({
                              ...prev,
                              description: e.target.value,
                            }))
                          }
                          className="form-textarea"
                          rows={3}
                        />
                      ) : (
                        <p className="form-value">{company.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "hours" && (
            <div className="tab-content">
              <div className="settings-card">
                <div className="card-header">
                  <div className="header-with-action">
                    <h3>Öffnungszeiten</h3>
                    {!isEditing.hours ? (
                      <button
                        onClick={() =>
                          setIsEditing((prev) => ({ ...prev, hours: true }))
                        }
                        className="edit-button"
                      >
                        <Edit3 className="h-4 w-4" />
                        Bearbeiten
                      </button>
                    ) : (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleCancel("hours")}
                          className="cancel-button"
                        >
                          <X className="h-4 w-4" />
                          Abbrechen
                        </button>
                        <button
                          onClick={() => handleSave("hours")}
                          className="save-button"
                        >
                          <Save className="h-4 w-4" />
                          Speichern
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-content">
                  <div className="hours-grid">
                    {(isEditing.hours
                      ? editForm.openingHours
                      : company.openingHours
                    ).map((hours, index) => (
                      <div key={hours.day} className="hours-row">
                        <div className="day-label">{hours.day}:</div>
                        <div className="hours-controls">
                          {isEditing.hours ? (
                            <>
                              <label className="closed-checkbox">
                                <input
                                  type="checkbox"
                                  checked={hours.closed}
                                  onChange={(e) =>
                                    updateOpeningHours(
                                      index,
                                      "closed",
                                      e.target.checked
                                    )
                                  }
                                />
                                <span>Geschlossen</span>
                              </label>
                              {!hours.closed && (
                                <div className="time-inputs">
                                  <input
                                    type="time"
                                    value={hours.open}
                                    onChange={(e) =>
                                      updateOpeningHours(
                                        index,
                                        "open",
                                        e.target.value
                                      )
                                    }
                                    className="time-input"
                                  />
                                  <span className="time-separator">–</span>
                                  <input
                                    type="time"
                                    value={hours.close}
                                    onChange={(e) =>
                                      updateOpeningHours(
                                        index,
                                        "close",
                                        e.target.value
                                      )
                                    }
                                    className="time-input"
                                  />
                                  <span className="time-label">Uhr</span>
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="hours-display">
                              {hours.closed ? (
                                <span className="closed-status">
                                  Geschlossen
                                </span>
                              ) : (
                                <>
                                  <span className="time-range">
                                    {hours.open} – {hours.close} Uhr
                                  </span>
                                  <span className="status-open">Geöffnet</span>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="tab-content">
              <div className="settings-card">
                <div className="card-header">
                  <div className="header-with-action">
                    <h3>Kontaktdaten</h3>
                    {!isEditing.contact ? (
                      <button
                        onClick={() =>
                          setIsEditing((prev) => ({ ...prev, contact: true }))
                        }
                        className="edit-button"
                      >
                        <Edit3 className="h-4 w-4" />
                        Bearbeiten
                      </button>
                    ) : (
                      <div className="action-buttons">
                        <button
                          onClick={() => handleCancel("contact")}
                          className="cancel-button"
                        >
                          <X className="h-4 w-4" />
                          Abbrechen
                        </button>
                        <button
                          onClick={() => handleSave("contact")}
                          className="save-button"
                        >
                          <Save className="h-4 w-4" />
                          Speichern
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="card-content">
                  <div className="contact-grid">
                    <div className="contact-item">
                      <div className="contact-icon">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div className="contact-details">
                        <label className="contact-label">E-Mail-Adresse</label>
                        {isEditing.contact ? (
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                email: e.target.value,
                              }))
                            }
                            className="contact-input"
                          />
                        ) : (
                          <a
                            href={`mailto:${company.email}`}
                            className="contact-value email-link"
                          >
                            {company.email}
                          </a>
                        )}
                      </div>
                    </div>

                    <div className="contact-item">
                      <div className="contact-icon">
                        <Phone className="h-5 w-5" />
                      </div>
                      <div className="contact-details">
                        <label className="contact-label">Telefonnummer</label>
                        {isEditing.contact ? (
                          <input
                            type="tel"
                            value={editForm.phone}
                            onChange={(e) =>
                              setEditForm((prev) => ({
                                ...prev,
                                phone: e.target.value,
                              }))
                            }
                            className="contact-input"
                          />
                        ) : (
                          <a
                            href={`tel:${company.phone}`}
                            className="contact-value phone-link"
                          >
                            {company.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
